import { useState } from "react";
import PropTypes from "prop-types";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
// material
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Tooltip,
} from "@mui/material";
//
import Iconify from "./common/Iconify/Iconify";
import Text from "./common/Text";
import { useAppDispatch, useAppSelector } from "@hooks";
import { commonRedux } from "@store";
import { Bookmark } from "@mui/icons-material";

// ----------------------------------------------------------------------

const ListItemStyle = styled((props: any) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 42,
  position: "relative",
  textTransform: "capitalize",
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
  // Yer imi ikonu her satırda sabit durunca menü kalabalık görünüyordu;
  // sadece hover'da ya da zaten sık kullanılanlardaysa görünür.
  "& .nav-bookmark": {
    opacity: 0,
    transition: theme.transitions.create("opacity"),
  },
  "&:hover .nav-bookmark, & .nav-bookmark.is-pinned": {
    opacity: 1,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active }: any) {
  const theme = useTheme();

  const isActiveRoot = active(item.path);
  const navigate = useNavigate();

  const { title, path, icon, info, children } = item;

  const [open, setOpen] = useState(isActiveRoot);
  const dispatch = useAppDispatch();

  const pageTabs = useAppSelector((state) => state.common.pageTabs);

  const handleOpen = () => {
    setOpen((prev: any) => !prev);
  };

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
  };

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
  };

  const handlePageTab = ({ title, path }: any) => {
    dispatch(commonRedux.setPageTab({ title, path }));
  };

  const isPinned = Boolean(pageTabs.find((tab: any) => tab.path === path));

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText
            disableTypography
            primary={<Text variant="body2">{title}</Text>}
          />
          {info && info}
          <Iconify
            icon={
              open
                ? "eva:arrow-ios-downward-fill"
                : "eva:arrow-ios-forward-fill"
            }
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item: any) => {
              const { title, path, icon } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  onClick={() => {
                    navigate(path);
                  }}
                  key={title}
                  sx={{
                    pl: 3,
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  {icon ? (
                    <ListItemIconStyle>{icon}</ListItemIconStyle>
                  ) : (
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: "flex",
                          borderRadius: "50%",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "text.disabled",
                          transition: (theme) =>
                            theme.transitions.create("transform"),
                          ...(isActiveSub && {
                            transform: "scale(2)",
                            bgcolor: "primary.main",
                          }),
                        }}
                      />
                    </ListItemIconStyle>
                  )}
                  <ListItemText
                    disableTypography
                    primary={<Text variant="body2">{title}</Text>}
                  />
                  <Tooltip
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePageTab({ title, path });
                    }}
                    title="Sık kullanılanlara ekle"
                  >
                    <Bookmark
                      sx={{
                        mr: 1,
                        color: pageTabs.find((tab: any) => tab.path === path)
                          ? theme.palette.primary.main
                          : theme.palette.grey[300],
                      }}
                      fontSize="small"
                    />
                  </Tooltip>
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      onClick={() => {
        navigate(path);
      }}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText
        disableTypography
        primary={<Text variant="body2">{title}</Text>}
      />
      <Tooltip
        onClick={(e) => {
          e.stopPropagation();
          handlePageTab({ title, path });
        }}
        title="Sık kullanılanlara ekle"
      >
        <Bookmark
          className={`nav-bookmark${isPinned ? " is-pinned" : ""}`}
          sx={{
            mr: 1,
            color: isPinned
              ? theme.palette.primary.main
              : theme.palette.grey[400],
          }}
          fontSize="small"
        />
      </Tooltip>
      {info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }: any) {
  const { pathname } = useLocation();

  const match = (path: any) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {navConfig.map((item: any) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
